import React, { createContext, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import { useQueryValues } from 'hooks/url.hook';
import { toQueryString } from 'utils';

type BuildingId = number | null;

type ReportsPageContextType = {
  /**
   * buildingId which comes from url query ?building=<buildingId>
   */
  buildingId: BuildingId;

  /**
   * report start and end date strings from url query ?start=<startDate>&end=<endDate>
   *
   * Note: string format follows ISO date string format to be considered backend side timezone.
   * e.g: 2022-06-20T01:46:04.936Z
   */
  start: string;
  end: string;
};

const defaultReportsPageContextValue = {
  buildingId: null,
  start: '',
  end: '',
};

export const ReportsPageContext = createContext<ReportsPageContextType>(defaultReportsPageContextValue);

interface ReportsPageContextProviderProps {
  children?: any;
}

export const ReportsPageContextProvider = ({ children = null }: ReportsPageContextProviderProps) => {
  const navigate = useNavigate();
  const { building: buildingIdStr, start, end } = useQueryValues(['building', 'start', 'end']);
  const buildingId = buildingIdStr ? Number(buildingIdStr) : null;

  useEffect(() => {
    if (!start && !end) {
      const start = dayjs().subtract(30, 'days').toISOString();
      const end = dayjs().toISOString();

      const qs = toQueryString({ building: buildingId, start, end });

      navigate(qs ? `?${qs}` : '');
    }
  }, [start, end]);

  return <ReportsPageContext.Provider value={{ buildingId, start, end }}>{children}</ReportsPageContext.Provider>;
};

export const useReportsPageContext = () => {
  return useContext(ReportsPageContext);
};
